/**
 * FRONT-2349: the fields a Reflections proposal's "Edit" opens. Markup only —
 * every change goes through the pure transforms in `./reflection-edit-draft`,
 * which is where the behaviour is tested. A new entry reuses the Quick OK
 * card's memory form (FRONT-1747) unchanged; a fact update and a Perfect Day
 * update get one field per value.
 */
import { Input } from '~/components/ui/input';
import { Textarea } from '~/components/ui/textarea';
import { ApprovalEditFormFields } from '~/features/chat/approval-edit-form';
import { setPerfectDayField, type ReflectionDraft } from './reflection-edit-draft';

const LABEL = 'text-[length:var(--text-xs)] font-medium text-muted-foreground';

export function ReflectionEditFields({
  draft,
  onChange,
  lockType = false,
}: {
  draft: ReflectionDraft;
  onChange: (next: ReflectionDraft) => void;
  /** Passed to the entry form: the type renders as plain text (Inbox Remember only). */
  lockType?: boolean;
}) {
  if (draft.kind === 'entity_create') {
    return (
      <ApprovalEditFormFields
        lockType={lockType}
        draft={draft.memory}
        onChange={(next) => {
          if (next.kind === 'memory') onChange({ ...draft, memory: next });
        }}
      />
    );
  }
  if (draft.kind === 'eddy_line') {
    return (
      <label className="flex flex-col gap-1">
        <span className={LABEL}>The lesson</span>
        <Input
          className="h-8"
          value={draft.text}
          aria-invalid={!draft.text.trim() || undefined}
          onChange={(e) => onChange({ ...draft, text: e.target.value })}
        />
      </label>
    );
  }
  if (draft.kind === 'attribute_update') {
    return (
      <label className="flex flex-col gap-1">
        <span className={LABEL}>{draft.label}</span>
        <Input
          className="h-8"
          value={draft.value}
          aria-invalid={!draft.value.trim() || undefined}
          onChange={(e) => onChange({ ...draft, value: e.target.value })}
        />
      </label>
    );
  }
  return (
    <div className="space-y-2">
      {draft.fields.map((f) => (
        <label key={f.key} className="flex flex-col gap-1">
          <span className={LABEL}>{f.label}</span>
          {f.list ? (
            <>
              <Textarea
                className="min-h-20"
                value={f.value}
                aria-invalid={!f.value.trim() || undefined}
                onChange={(e) => onChange(setPerfectDayField(draft, f.key, e.target.value))}
              />
              <span className="text-[length:var(--text-xs)] text-muted-foreground">One per line.</span>
            </>
          ) : (
            <Input
              className="h-8"
              value={f.value}
              aria-invalid={!f.value.trim() || undefined}
              onChange={(e) => onChange(setPerfectDayField(draft, f.key, e.target.value))}
            />
          )}
        </label>
      ))}
    </div>
  );
}
